import React from 'react';
import { DataGrid, GridColDef, GridRowsProp } from '@mui/x-data-grid';
import { Task } from '@tassomutuo-backoffice/types';
import { Box, Paper, IconButton, Tooltip, Chip } from '@mui/material';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import ScheduleIcon from '@mui/icons-material/Schedule';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VisibilityIcon from '@mui/icons-material/Visibility';

interface ListViewProps {
  tasks: Task[];
  loading?: boolean;
  rowCount?: number;
  paginationModel?: {
    page: number;
    pageSize: number;
  };
  onPaginationModelChange?: (model: { page: number; pageSize: number }) => void;
  onTaskToggle?: (taskId: string, completed: boolean) => void;
  onTaskEdit?: (task: Task) => void;
  onTaskClick?: (task: Task) => void;
}

const ListView: React.FC<ListViewProps> = ({
  tasks,
  loading = false,
  rowCount: rowCountProp,
  paginationModel = { page: 0, pageSize: 25 },
  onPaginationModelChange,
  onTaskToggle,
  onTaskEdit,
  onTaskClick,
}) => {
  const navigate = useNavigate();

  // Memoize rowCount to prevent it from becoming undefined during loading
  const rowCountRef = React.useRef(rowCountProp || 0);

  const rowCount = React.useMemo(() => {
    if (!loading && rowCountProp !== undefined) {
      rowCountRef.current = rowCountProp;
    }
    return rowCountRef.current;
  }, [rowCountProp]);

  const rows: GridRowsProp = tasks.map((task) => ({
    ...task,
    leadName: task.lead?.properties?.dealname || '',
    userName:
      typeof task.userId === 'object' ? task.userId.name : 'Sconosciuto',
    userEmail:
      typeof task.userId === 'object' ? task.userId.email : 'Sconosciuto',
  }));

  const columns: GridColDef[] = [
    {
      field: 'date',
      headerName: 'Data',
      width: 150,
      type: 'date',
      valueGetter: (params) => {
        // Assicura che il valore sia un oggetto Date per l'ordinamento corretto
        return params.value ? new Date(params.value) : null;
      },
      valueFormatter: (params) => {
        if (!params.value) return '-';
        return format(new Date(params.value), 'dd MMMM yyyy', { locale: it });
      },
      sortComparator: (v1, v2) => {
        // Gestisce correttamente null/undefined nell'ordinamento
        if (!v1 && !v2) return 0;
        if (!v1) return -1;
        if (!v2) return 1;
        return v1.getTime() - v2.getTime();
      },
    },
    {
      field: 'completed',
      headerName: 'Stato',
      width: 130,
      type: 'boolean',
      sortable: true,
      renderCell: (params) => {
        const isCompleted = params.value || false;
        return (
          <Chip
            icon={
              isCompleted ? (
                <CheckIcon sx={{ fontSize: '1rem' }} />
              ) : (
                <ScheduleIcon sx={{ fontSize: '1rem' }} />
              )
            }
            label={isCompleted ? 'Fatto' : 'Da fare'}
            size="small"
            sx={{
              borderRadius: '5px',
              backgroundColor: isCompleted ? '#DDF8F2' : '#FFEECC',
              color: isCompleted ? '#059573' : '#F9A602',
              fontWeight: 500,
              fontSize: '0.75rem',
              '& .MuiChip-icon': {
                color: isCompleted ? '#059573' : '#F9A602',
              },
            }}
          />
        );
      },
      sortComparator: (v1, v2) => {
        // Ordina con "Da fare" prima (false) e "Fatto" dopo (true)
        const a = v1 || false;
        const b = v2 || false;
        return Number(a) - Number(b);
      },
    },
    {
      field: 'leadName',
      headerName: 'Nome Lead',
      flex: 1,
      type: 'string',
      sortComparator: (v1, v2) => {
        // Ordinamento case-insensitive con gestione valori vuoti
        const a = (v1 || '').toLowerCase();
        const b = (v2 || '').toLowerCase();
        return a.localeCompare(b, 'it');
      },
    },
    {
      field: 'action',
      headerName: 'Azione',
      flex: 1,
      minWidth: 200,
      type: 'string',
      sortComparator: (v1, v2) => {
        const a = (v1 || '').toLowerCase();
        const b = (v2 || '').toLowerCase();
        return a.localeCompare(b, 'it');
      },
    },
    {
      field: 'notes',
      headerName: 'Note',
      flex: 1,
      minWidth: 200,
      type: 'string',
      renderCell: (params) => (
        <Tooltip title={params.value || ''}>
          <Box
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {params.value || '-'}
          </Box>
        </Tooltip>
      ),
      sortComparator: (v1, v2) => {
        const a = (v1 || '').toLowerCase();
        const b = (v2 || '').toLowerCase();
        return a.localeCompare(b, 'it');
      },
    },
    {
      field: 'actions',
      headerName: 'Azioni',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Visualizza lead">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                const task = params.row as Task;
                navigate(`/lead/${task.lead.id}?from=tasks-list`);
              }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Visualizza dettagli">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onTaskEdit?.(params.row as Task);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Paper elevation={0} sx={{ p: 3, height: 'calc(100vh - 200px)' }}>
      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        rowCount={rowCount}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={[10, 25, 50, 100]}
        sortingMode="server"
        onRowClick={(params) => onTaskClick?.(params.row as Task)}
        disableRowSelectionOnClick
        sx={{
          border: 'none',
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-row:hover': {
            cursor: 'pointer',
          },
        }}
        localeText={{
          noRowsLabel: 'Nessun promemoria trovato',
          MuiTablePagination: {
            labelRowsPerPage: 'Righe per pagina:',
            labelDisplayedRows: ({ from, to, count }) =>
              `${from}-${to} di ${count !== -1 ? count : `più di ${to}`}`,
          },
        }}
      />
    </Paper>
  );
};

export default ListView;
